import React, { useMemo, useState } from "react";
import { Card, ListGroup, Row, Col, Button } from "react-bootstrap";
import { Location } from "../../types/Types";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHammer, faPlane, faHotel, faUmbrellaBeach, faGraduationCap, faPencilAlt, faCalendar, faTrain } from '@fortawesome/free-solid-svg-icons';
import { getCountryFlag } from "../../util/Util";

interface Props {
    locations: Location[];
}

function AdminOpeningSoon(props: Props) {
    const [hasLoaded, setHasLoaded] = useState<boolean>(false);

    const filteredLocations = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset to start of day for comparison

        const locs = props.locations.filter((location) => {
            // Only include pubs that:
            // 1. Are not permanently closed
            // 2. Have an opening date
            // 3. Opening date is today or in the future
            if (!location.pubIsClosed && location.openingDate) {
                const openingDate = new Date(location.openingDate);
                openingDate.setHours(0, 0, 0, 0);
                
                return openingDate >= today;
            }
            return false;
        });

        // Sort by opening date (earliest first)
        locs.sort((a, b) => {
            if (!a.openingDate || !b.openingDate) return 0;
            return new Date(a.openingDate).getTime() - new Date(b.openingDate).getTime();
        });

        return locs;
    }, [props.locations]);

    const showData = () => {
        setHasLoaded(true);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { 
            day: 'numeric', 
            month: 'short', 
            year: 'numeric' 
        });
    };

    const isToday = (dateString: string) => {
        const date = new Date(dateString);
        const today = new Date();
        date.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        return date.getTime() === today.getTime();
    };

    return (
        <>
            <Card className={"mb-3"}>
	                <Card.Header>
	                    <div className="d-flex justify-content-between align-items-center">
	                        <div>
	                            <div><b>Opening Soon</b></div>
	                            <small className="text-muted">Total: {filteredLocations.length} pubs</small>
	                        </div>
                            {!hasLoaded && (
                                <Button size="sm" onClick={showData}>Show Data</Button>
                            )}
	                    </div>
	                </Card.Header>
                    {hasLoaded && (
	                <Card.Body style={{ padding: 0 }}>
	                    {filteredLocations.length === 0 ? (
	                        <div className="text-center p-3">No pubs opening soon</div>
                    ) : (
                        <ListGroup variant="flush">
                            {filteredLocations.map((location) => (
                                <ListGroup.Item key={location.id} className="admin-pub-data-list-item">
                                    <Row className="align-middle flex-nowrap no-gutters">
                                        <Col className="d-flex flex-column align-items-start justify-content-center text-truncate">
                                            <div>
                                                <Link to={`/location/${location.id}/comments`} className={"p-0"}>
                                                    <div className={"location-tag"}>{location.name}</div>
                                                </Link>
                                                {location.airport && <small className="text-truncate">
                                                    <FontAwesomeIcon className="ml-1 animated zoomIn icon-plane" icon={faPlane} />
                                                </small>}
                                                {location.hotel && <small className="text-truncate">
                                                    <FontAwesomeIcon className="ml-1 animated zoomIn icon-hotel" icon={faHotel} />
                                                </small>}
                                                {location.holidayPark && <small className="text-truncate">
                                                    <FontAwesomeIcon className="ml-1 animated zoomIn icon-holidaypark" icon={faUmbrellaBeach} />
                                                </small>}
                                                {location.isUniversity && <small className="text-truncate">
                                                    <FontAwesomeIcon className="ml-1 animated zoomIn icon-university" icon={faGraduationCap} />
                                                </small>}
                                                {location.trainStation && <small className="text-truncate">
                                                    <FontAwesomeIcon className="ml-1 animated zoomIn icon-train" icon={faTrain} />
                                                </small>}
                                                {location.pubIsTemporaryClosed && <small className="text-truncate">
                                                    <FontAwesomeIcon className="ml-1 animated zoomIn icon-temporary" icon={faHammer} />
                                                </small>}
                                                <Link to={`/admin/${location.id}`}>
                                                    <FontAwesomeIcon className="ml-2 animated zoomIn icon-pen cursor-pointer" icon={faPencilAlt} />
                                                </Link>
                                            </div>
                                            <div className="ml-1">
                                                <small className="text-truncate">
                                                    <div className="d-inline-block" style={{ marginRight: "0.3em" }}>{getCountryFlag(location.country || '')}</div>
                                                    <div className="d-inline-block">{location.address}</div>
                                                </small>
                                            </div>
                                        </Col>
                                        <Col xs="auto" className="d-flex align-items-center">
                                            <span 
                                                className="d-flex align-items-center"
                                                style={{ 
                                                    fontSize: '0.85rem',
                                                    color: isToday(location.openingDate || '') ? '#28a745' : '#a1aab3',
                                                    fontWeight: isToday(location.openingDate || '') ? '600' : '500'
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faCalendar} className="mr-1" />
                                                {isToday(location.openingDate || '') ? 'Opens Today' : formatDate(location.openingDate || '')}
                                            </span>
                                        </Col>
                                    </Row>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </Card.Body>
                    )}
            </Card>
        </>
    );
}

export default AdminOpeningSoon;
